// offline-conflict.ts — the per-tab updateJSON rebase registry and the
// conflict pipeline the drain runs when a queued write meets a newer server
// value.
//
// Registry. A queued compare-and-swap result carries its op's id on the
// module-private, non-enumerable OPID symbol: { opId, slot }, where slot is
// this write's index among the writes folded into the op (offline-store
// coalesce). updateJSON reads it and registers its `mutate` here, so a later
// conflict can re-run the closures — composed in slot order — on the server's
// value (CON-5, RM-7). A chain is rebaseable only when EVERY slot has a
// closure: a plain put or delete folded into the op leaves a gap, and the
// final value then no longer derives from the closures. The registry lives
// only in this tab's memory.
//
// Pipeline (runConflictPipeline). The conflict takes in the key's later
// queued ops (never the one on the wire): local is the last one's value (null:
// a delete), and every resolution commits or drops the whole group. In order:
//   1. rebase — when every op of the group has a complete chain and was queued
//      by this tab, re-run the closures on the witness and CAS the result
//      against it (a 412 re-runs them on the new witness, up to the last
//      chain's maxAttempts); when the group is one JSON op queued by another
//      tab, ask that tab (rebase-request / rebase-response, ≤5 s a round);
//   2. options.onConflict — ≤10 s and ≤3 rounds (a 412 starts the next round
//      with the new witness): a {value, contentType} is CASed ("merged"),
//      "local" CASes the queued value or delete ("local"); a local delete
//      against an absent key commits without a request;
//   3. otherwise — "remote", undefined, a throw, a timeout, an invalid body,
//      the rounds spent — the remote value wins.
// A CAS answer other than a 2xx / 412 (and a DELETE 404, a success) goes to
// the drain's outcome table (ConflictIO.settle). Every resolution emits
// conflict{resolution} and a corrective storage.change (offline-sync.ts), and
// folds into the mirror as evidence from the start of the request whose
// answer it adopts (the witness's, or the settling CAS's — Conflict.t0 /
// Replayed.t0), so the mirror orders it against newer reads.
//
// initConflictChannel answers other tabs' rebase-requests for ops this tab
// holds a complete chain for (silence otherwise).

import { encodeJSON, JSON_CONTENT_TYPE } from "./json.js";
import { normalizeBody } from "./body.js";
import { randomHex, type RebaseResponseMessage } from "./offline-channel.js";
import { getSyncContext, type Conflict, type ConflictIO, type Replayed } from "./offline-sync.js";
import type { OfflineConflict, OfflineResolution, OfflineValue } from "./offline.js";

export const OPID = Symbol("backlit.opId"); // value: { opId: string; slot: number }

type Fn = (v: unknown) => unknown;

const chains = new Map<string, { fns: Fn[]; max: number }>();

let rebaseWaitMs = 5000;
let resolverMs = 10_000;

// registerRebase records slot's closure for opId; maxAttempts is the latest
// registration's.
export function registerRebase(opId: string, slot: number, mutate: Fn, maxAttempts: number): void {
  const c = chains.get(opId) ?? { fns: [], max: 0 };
  c.fns[slot] = mutate;
  c.max = maxAttempts;
  chains.set(opId, c);
}

// rebaseChain returns opId's closures for slots 0..writes-1, or null unless
// every one of them is registered.
export function rebaseChain(opId: string, writes: number): { fns: Fn[]; maxAttempts: number } | null {
  const c = chains.get(opId);
  if (!c || writes < 1) return null;
  const fns = c.fns.slice(0, writes);
  for (let i = 0; i < writes; i++) if (typeof fns[i] !== "function") return null;
  return { fns, maxAttempts: c.max };
}

export function forgetRebase(opId: string): void {
  chains.delete(opId);
}

// rebased runs fns on the JSON text (null: the key is absent → undefined) and
// returns the result as encodeJSON text; it throws on bad JSON or a throwing
// closure.
function rebased(fns: Fn[], text: string | null): string {
  let v = text === null ? undefined : (JSON.parse(text) as unknown);
  for (const f of fns) v = f(v);
  return encodeJSON(v);
}

const utf8 = (s: string) => new TextEncoder().encode(s).buffer as ArrayBuffer;

// runConflictPipeline settles c (see the header); it resolves whether the
// drain pass goes on.
export async function runConflictPipeline(c: Conflict, io: ConflictIO): Promise<boolean> {
  const x = getSyncContext()!;
  const op = c.op;
  const ch = x.channel();
  const group = [op, ...(await x.outbox.forKey(op.uid, op.store, op.key)).filter((g) => g.seq > op.seq && g.state !== "inflight")];
  const last = group[group.length - 1]!;
  const lb = last.bytes;
  const local: OfflineValue | null = lb && { value: new Blob([lb], { type: last.contentType }), contentType: last.contentType, crc32c: last.crc32c };
  let remote = c.remote;
  let uid = c.uid;
  // t0: when the request that answered remote started (the interval its
  // mirror fold carries); ep: the purge epoch then.
  let t0 = c.t0;
  let ep = c.ep;
  let out = true;
  // cas sends bytes (null: a DELETE) against the latest remote. A settled
  // answer sets out and resolves true; a 412 witness, or a based put finding
  // the key gone, becomes the new remote (false). A delete of an absent key
  // needs no request (its evidence is the answer that found it absent).
  const cas = async (bytes: ArrayBuffer | null, ct: string, how: "local" | "merged"): Promise<boolean> => {
    const r =
      bytes || remote
        ? await io.cas(op, bytes, ct, remote ? remote.crc32c : "")
        : ({ status: 204, crc32c: "", owner: "", uid, t0, ep } as Replayed);
    if (r.status === 412 && r.witness) {
      remote = r.witness;
      uid = r.uid;
      t0 = r.t0;
      ep = r.ep;
      return false;
    }
    if (r.status === 404 && r.code === "not_found" && bytes && remote) {
      remote = null;
      t0 = r.t0;
      ep = r.ep;
      return false;
    }
    out = await io.settle(op, r, bytes, ct, group, how);
    return true;
  };

  // 1. Rebase: next() is the rebased value's text for the current remote.
  const ks = group.map((g) => rebaseChain(g.opId, g.writes));
  let max = 1;
  let next: (() => Promise<string | null>) | null = null;
  if (ks.every((k) => k) && group.every((g) => g.originTab === x.tabId)) {
    const fns = ks.flatMap((k) => k!.fns);
    max = ks[ks.length - 1]!.maxAttempts;
    next = async () => rebased(fns, remote && (await remote.value.text()));
  } else if (ch && !group[1] && op.originTab !== x.tabId && op.contentType === JSON_CONTENT_TYPE) {
    next = async () => {
      const rid = randomHex().slice(16);
      const w = remote && { bytes: await remote.value.arrayBuffer(), contentType: remote.contentType, crc32c: remote.crc32c };
      let off = () => {};
      const m = await new Promise<RebaseResponseMessage | null>((res) => {
        off = ch.on((m) => {
          if (m.type === "rebase-response" && m.rid === rid) res(m);
        });
        setTimeout(res, rebaseWaitMs, null);
        ch.post({ type: "rebase-request", rid, opId: op.opId, writes: op.writes, remote: w });
      });
      off();
      if (m) max = m.maxAttempts;
      return m && m.value;
    };
  }
  for (let i = 0; next && i < max; i++) {
    let t: string | null = null;
    try {
      t = await next();
    } catch {
      /* bad JSON or a throwing closure: no rebase */
    }
    if (t === null) break;
    if (await cas(utf8(t), JSON_CONTENT_TYPE, "merged")) return out;
  }

  // 2. onConflict rounds: "local" CASes the queued value (or delete), a
  // {value, contentType} the normalized body.
  const h = x.options().onConflict;
  for (let round = 0; h && round < 3; round++) {
    let bytes = lb;
    let ct = last.contentType;
    let how: "local" | "merged" = "local";
    try {
      const rv: OfflineValue | null = remote && { value: remote.value, contentType: remote.contentType, crc32c: remote.crc32c };
      const conflict: OfflineConflict = {
        store: op.store as OfflineConflict["store"],
        key: op.key,
        operation: lb ? "set" : "delete",
        local,
        remote: rv,
        base: op.base ?? "",
      };
      const res = await new Promise<OfflineResolution>((ok, no) => {
        setTimeout(no, resolverMs);
        Promise.resolve()
          .then(() => h(conflict))
          .then(ok, no);
      });
      if (res !== "local") {
        if (!res || typeof res !== "object") break;
        const n = normalizeBody(res.value, res.contentType);
        bytes = await new Blob([n.body as BlobPart]).arrayBuffer();
        ct = n.contentType;
        how = "merged";
      }
    } catch {
      break; // a throw, a timeout or an invalid body
    }
    if (await cas(bytes, ct, how)) return out;
  }

  // 3. Remote wins.
  await io.remoteWins({ op, remote, uid, t0, ep }, group);
  return true;
}

// initConflictChannel answers rebase-requests for ops whose complete chain
// this tab holds; it returns the listener's teardown.
export function initConflictChannel(): () => void {
  const ch = getSyncContext()?.channel();
  return ch
    ? ch.on((m) => {
        if (m.type !== "rebase-request") return;
        const k = rebaseChain(m.opId, m.writes);
        if (!k) return;
        let value: string | null = null;
        try {
          value = rebased(k.fns, m.remote && new TextDecoder().decode(m.remote.bytes));
        } catch {
          /* answer null: the requester moves on */
        }
        ch.post({ type: "rebase-response", rid: m.rid, value, maxAttempts: value === null ? 0 : k.maxAttempts });
      })
    : () => {};
}

// --- test hooks -------------------------------------------------------------------

export function _setConflictTimingForTest(t: { rebaseWaitMs?: number; resolverMs?: number } | null): void {
  rebaseWaitMs = t?.rebaseWaitMs ?? 5000;
  resolverMs = t?.resolverMs ?? 10_000;
}

export function _resetRebaseRegistryForTest(): void {
  chains.clear();
}
